import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { StorySummaryData, ScriptHighlight } from '@packages/global';

/**
 * 流式内容数据
 */
export interface StreamingContentData {
  /** 流式标题 */
  title?: string;
  /** 流式欢迎语 */
  welcomeMessage?: string;
  /** 流式摘要 */
  summary?: string;
  /** 当前正在流式输出的字段 */
  currentField?: 'title' | 'welcomeMessage' | 'summary';
}

/**
 * StorySummaryStep 组件属性
 */
export interface StorySummaryStepProps {
  /** 步骤数据 */
  data: StorySummaryData | null;
  /** 是否正在编辑 */
  isEditing?: boolean;
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 是否正在生成中（Step 1 流式生成） */
  isGenerating?: boolean;
  /** 流式内容数据 */
  streamingContent?: StreamingContentData;
  /** 编辑回调 */
  onEdit?: () => void;
  /** 取消编辑回调 */
  onCancelEdit?: () => void;
  /** 保存回调 */
  onSave?: () => void;
  /** 重新生成回调 */
  onRegenerate?: () => void;
  /** 数据变更回调 */
  onChange?: (data: Partial<StorySummaryData>) => void;
}

/**
 * 空状态组件
 */
function EmptyState({ onRegenerate }: { onRegenerate?: () => void }) {
  const { t } = useTranslation(['script', 'common']);
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-bg-elevated flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-gray-200 mb-2">{t('script:storySummary.empty')}</h3>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">
        {t('script:storySummary.emptyDesc')}
      </p>
      {onRegenerate && (
        <button
          onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" />
          {t('script:storySummary.generate')}
        </button>
      )}
    </div>
  );
}

/**
 * 流式文本显示组件 - 带打字机光标效果
 */
function StreamingText({
  text,
  isStreaming,
  placeholder,
  className = '',
}: {
  text?: string;
  isStreaming?: boolean;
  placeholder?: string;
  className?: string;
}) {
  return (
    <span className={className}>
      {text || placeholder || ''}
      {isStreaming && (
        <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
      )}
    </span>
  );
}

/**
 * 生成中状态组件 - 显示流式内容
 */
function GeneratingState({
  streamingContent,
}: {
  streamingContent?: StreamingContentData;
}) {
  const { t } = useTranslation(['script', 'common']);
  const { title, welcomeMessage, summary, currentField } = streamingContent || {};

  return (
    <div className="space-y-5">
      {/* 加载动画头部 */}
      <div className="flex items-center gap-3 pb-4 border-b border-border-subtle">
        <div className="relative">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-[#FF8C00] flex items-center justify-center">
            <ArrowPathIcon className="w-5 h-5 text-white animate-spin" />
          </div>
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-success border-2 border-bg-page" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-50">{t('script:storySummary.aiThinking')}</h3>
          <p className="text-xs text-gray-400">{t('script:storySummary.aiThinkingDesc')}</p>
        </div>
      </div>

      {/* 流式标题 */}
      {(title || currentField === 'title') && (
        <section className="space-y-2">
          <label className="text-[13px] font-medium text-gray-200 flex items-center gap-2">
            {t('script:storySummary.storyTitle')}
            {currentField === 'title' && (
              <span className="text-xs text-primary">{t('script:status.generating')}</span>
            )}
          </label>
          <div className="rounded-xl bg-bg-elevated p-4">
            <StreamingText
              text={title}
              isStreaming={currentField === 'title'}
              placeholder={t('script:storySummary.generatingTitle')}
              className="text-base font-medium text-gray-50"
            />
          </div>
        </section>
      )}

      {/* 流式欢迎语 */}
      {(welcomeMessage || currentField === 'welcomeMessage') && (
        <section className="space-y-2">
          <label className="text-[13px] font-medium text-gray-200 flex items-center gap-2">
            {t('script:storySummary.welcomeMessage')}
            {currentField === 'welcomeMessage' && (
              <span className="text-xs text-primary">{t('script:status.generating')}</span>
            )}
          </label>
          <div className="rounded-xl bg-bg-elevated p-4">
            <StreamingText
              text={welcomeMessage}
              isStreaming={currentField === 'welcomeMessage'}
              placeholder={t('script:storySummary.generatingWelcome')}
              className="text-sm text-gray-50 leading-relaxed"
            />
          </div>
        </section>
      )}

      {/* 流式摘要 */}
      {(summary || currentField === 'summary') && (
        <section className="space-y-2">
          <label className="text-[13px] font-medium text-gray-200 flex items-center gap-2">
            {t('script:storySummary.title')}
            {currentField === 'summary' && (
              <span className="text-xs text-primary">{t('script:status.generating')}</span>
            )}
          </label>
          <div className="rounded-xl bg-bg-elevated p-4">
            <StreamingText
              text={summary}
              isStreaming={currentField === 'summary'}
              placeholder={t('script:storySummary.generatingSummary')}
              className="text-sm text-gray-50 leading-relaxed"
            />
          </div>
        </section>
      )}

      {/* 等待更多内容的占位 */}
      {!title && !welcomeMessage && !summary && !currentField && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className="flex gap-1">
              <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span>{t('script:storySummary.analyzingNeeds')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * 亮点卡片组件
 */
function HighlightCard({
  highlight,
  isEditing,
  onChange,
}: {
  highlight: ScriptHighlight;
  isEditing?: boolean;
  onChange?: (updated: ScriptHighlight) => void;
}) {
  const { t } = useTranslation(['script', 'common']);
  if (isEditing) {
    return (
      <div className="rounded-xl bg-bg-elevated p-4 space-y-3">
        <input
          type="text"
          value={highlight.name}
          onChange={(e) => onChange?.({ ...highlight, name: e.target.value })}
          className="w-full bg-transparent text-sm font-medium text-gray-50 outline-none border-b border-border-subtle pb-2 focus:border-primary transition-colors"
          placeholder={t('script:storySummary.highlightNamePlaceholder')}
        />
        <textarea
          value={highlight.description}
          onChange={(e) => onChange?.({ ...highlight, description: e.target.value })}
          className="w-full bg-transparent text-[13px] text-gray-200 leading-relaxed outline-none resize-none min-h-[60px]"
          placeholder={t('script:storySummary.highlightDescPlaceholder')}
        />
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-bg-elevated p-4 space-y-2">
      <h4 className="text-sm font-medium text-gray-50">{highlight.name}</h4>
      <p className="text-[13px] text-gray-200 leading-relaxed">{highlight.description}</p>
    </div>
  );
}

/**
 * StorySummaryStep 组件
 * 
 * 显示故事概要内容，包括：
 * - 故事摘要
 * - 故事亮点（可编辑）
 * 
 * 设计参考：Pencil 设计 DMFPQ 中的 CenterBody 节点
 * - gap: 20
 * - padding: 24
 * 
 * Requirements: 6.3, 6.9, 6.10
 */
export default function StorySummaryStep({
  data,
  isEditing = false,
  isLoading = false,
  isGenerating = false,
  streamingContent,
  onEdit,
  onCancelEdit,
  onSave,
  onRegenerate,
  onChange,
}: StorySummaryStepProps) {
  const [localData, setLocalData] = useState<StorySummaryData | null>(data);
  const { t } = useTranslation(['script', 'common']);

  // 同步外部数据变化（非编辑模式时）
  useEffect(() => {
    if (!isEditing && data) {
      setLocalData(data);
    }
  }, [data, isEditing]);

  // 处理摘要变更
  const handleSummaryChange = (summary: string) => {
    if (localData) {
      const updated = { ...localData, summary };
      setLocalData(updated);
      onChange?.({ summary });
    }
  };

  // 处理亮点变更
  const handleHighlightChange = (index: number, updated: ScriptHighlight) => {
    if (localData) {
      const highlights = [...localData.highlights];
      highlights[index] = updated;
      setLocalData({ ...localData, highlights });
      onChange?.({ highlights });
    }
  };

  // 生成中状态 - 显示流式内容
  if (isGenerating) {
    return <GeneratingState streamingContent={streamingContent} />;
  }

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-gray-400">{t('script:storySummary.generating')}</span>
        </div>
      </div>
    );
  }

  // 空状态
  if (!data) {
    return <EmptyState onRegenerate={onRegenerate} />;
  }

  // 编辑功能在生成中时禁用
  const canEdit = !isGenerating && !isLoading;

  return (
    <div className="space-y-5">
      {/* 故事摘要区域 */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-[13px] font-medium text-gray-200">{t('script:storySummary.title')}</label>
          {onEdit && !isEditing && canEdit && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-50 transition-colors cursor-pointer"
            >
              <PencilIcon className="w-3.5 h-3.5" />
              {t('common:edit')}
            </button>
          )}
        </div>
        <div className="rounded-xl bg-bg-elevated p-4">
          {isEditing ? (
            <textarea
              value={localData?.summary || ''}
              onChange={(e) => handleSummaryChange(e.target.value)}
              className="w-full bg-transparent text-sm text-gray-50 leading-relaxed outline-none resize-none min-h-[80px]"
              placeholder={t('script:storySummary.placeholder')}
            />
          ) : (
            <p className="text-sm text-gray-50 leading-relaxed">
              {data.summary || t('script:storySummary.noSummary')}
            </p>
          )}
        </div>
      </section>

      {/* 故事亮点区域 */}
      <section className="space-y-3">
        <label className="text-[13px] font-medium text-gray-200">{t('script:storySummary.highlights')}</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.highlights.length > 0 ? (
            data.highlights.map((highlight, index) => (
              <HighlightCard
                key={index}
                highlight={localData?.highlights[index] || highlight}
                isEditing={isEditing}
                onChange={(updated) => handleHighlightChange(index, updated)}
              />
            ))
          ) : (
            <div className="col-span-2 text-center py-8 text-sm text-gray-400">
              {t('script:storySummary.noHighlights')}
            </div>
          )}
        </div>
      </section>

      {/* 编辑模式下的保存/取消按钮 */}
      {isEditing && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
          <button
            onClick={onCancelEdit}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-50 transition-colors cursor-pointer"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
          >
            {t('common:save')}
          </button>
        </div>
      )}
    </div>
  );
}
